import { apiSlice } from './apiSlice';
import {
  loginFailure,
  loginStart,
  loginSuccess,
  selectRefreshToken,
  selectToken,
  tokenRefreshed,
  twoFactorRequired,
} from '../slices/authSlice';
import {
  authErrorMessage,
  GOOGLE_FAILED_MESSAGE,
  sessionFromRefresh,
  sessionFromTwoFactor,
  SIGNUP_FAILED_MESSAGE,
  TWO_FACTOR_FAILED_MESSAGE,
} from './authHelpers';
import { kycaidApi } from './kycaidApi';
import { t } from '../../i18n/i18n';

export const authApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Forgot password: `POST /password/reset-request/` ← { email }, as the old
     * dashboard sent it. Public — there is no session yet. Success echoes the
     * `email`; a refusal can come back 200 with `error.message`.
     */
    requestPasswordReset: builder.mutation({
      query: ({ email }) => ({
        url: '/password/reset-request/',
        method: 'POST',
        body: { email },
      }),
    }),

    login: builder.mutation({
      /**
       * `rememberMe` is the form's own field, not something the endpoint is
       * asked about — it is pulled off here and spent on the session's own
       * lifetime below, so the body on the wire is unchanged.
       */
      query: ({ rememberMe, ...credentials }) => ({
        url: '/email/signin/',
        method: 'POST',
        body: credentials,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        dispatch(loginStart());
        try {
          const { data } = await queryFulfilled;
          // An account with 2FA on gets no credentials here — just a
          // `temp_token` to spend on `verify2fa`. The caller reads
          // `requires_2fa` off the same response and opens the challenge.
          if (data?.requires_2fa) {
            dispatch(twoFactorRequired());
            return;
          }
          dispatch(loginSuccess({ ...data, remember: Boolean(arg?.rememberMe) }));
          dispatch(kycaidApi.endpoints.getIsNigeria.initiate());
        } catch (err) {
          dispatch(loginFailure(authErrorMessage(err?.error)));
        }
      },
      invalidatesTags: ['Auth'],
    }),

    /**
     * Second half of a 2FA sign-in: the `temp_token` from `login` plus the code
     * the authenticator is showing, exchanged for the real credentials.
     *
     * Public (see PUBLIC_ENDPOINTS) — there is no session to authorise it with
     * yet, and its own 401s must not trip the reauth handler.
     *
     * Failures come back as HTTP 400 with `{ success, message }`, so `unwrap()`
     * rejects and `authErrorMessage` reads the backend's wording.
     */
    verify2fa: builder.mutation({
      // `remember` comes from the form behind the dialog, which is where the
      // trader ticked it — the challenge itself never asks.
      query: ({ tempToken, otpCode }) => ({
        url: '/auth/2fa/verify/',
        method: 'POST',
        body: { temp_token: tempToken, otp_code: otpCode },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        dispatch(loginStart());
        try {
          const { data } = await queryFulfilled;
          if (!data?.access_token) {
            dispatch(loginFailure(t(TWO_FACTOR_FAILED_MESSAGE)));
            return;
          }
          dispatch(
            loginSuccess({
              ...sessionFromTwoFactor(data),
              remember: Boolean(arg?.remember),
            }),
          );
          dispatch(kycaidApi.endpoints.getIsNigeria.initiate());
        } catch (err) {
          dispatch(loginFailure(authErrorMessage(err?.error)));
        }
      },
      invalidatesTags: ['Auth'],
    }),

    /**
     * Profile → Password. Authenticated (not in PUBLIC_ENDPOINTS), so the
     * Bearer header goes on it like any other signed-in call.
     *
     * No `onQueryStarted`: changing a password doesn't mint a new session, and
     * the endpoint reports failure with a 200, so the caller reads the body
     * through `passwordChangeOutcome` rather than catching a rejection.
     */
    changePassword: builder.mutation({
      /**
       * `queryFn` rather than `query` because the body needs the session's
       * `idToken`, and only this form gets a look at the store. The same token
       * already rides along as `Authorization: Bearer …` (see apiSlice) — the
       * endpoint wants it in the body as well, so it is sent twice by design
       * rather than by accident.
       *
       * Reading it here keeps the token out of the form: the Password section
       * has no business holding credentials to pass down.
       */
      async queryFn({ currentPassword, newPassword, confirmPassword }, api, extraOptions, baseQuery) {
        return baseQuery({
          url: '/change/password/',
          method: 'POST',
          body: {
            current_password: currentPassword,
            new_password: newPassword,
            confirm_password: confirmPassword,
            idToken: selectToken(api.getState()),
          },
        });
      },
    }),

    /**
     * "Stay signed in" on the session-expiry dialog (see
     * layout/SessionExpiryDialog.jsx). The same `/get/refreshtoken/` call the
     * 401 handler makes for a remembered session, but asked for out loud: a
     * session without "Remember for 30 days" only renews when the trader says
     * so, never behind their back.
     *
     * In SELF_HANDLED_401 — a refused renewal is the dialog's to answer (it
     * signs the session out), not a reason to try refreshing the refresh.
     */
    renewSession: builder.mutation({
      async queryFn(_arg, api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: '/get/refreshtoken/',
          method: 'POST',
          body: { refresh_token: selectRefreshToken(api.getState()) },
        });
        if (result.error) return result;

        const session = sessionFromRefresh(result.data);
        if (!session) return { error: { status: 'CUSTOM_ERROR', error: 'No token in refresh response' } };
        api.dispatch(tokenRefreshed(session));
        return { data: null };
      },
    }),

    signup: builder.mutation({
      query: (body) => ({
        url: '/v2/email/signup/',
        method: 'POST',
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        dispatch(loginStart());
        try {
          const { data } = await queryFulfilled;
          if (!data?.firebase_cred?.idToken) {
            dispatch(loginFailure(t(SIGNUP_FAILED_MESSAGE)));
            return;
          }
          dispatch(loginSuccess(data));
        } catch (err) {
          dispatch(loginFailure(authErrorMessage(err?.error)));
        }
      },
      invalidatesTags: ['Auth'],
    }),

    /**
     * Second half of the Google redirect flow: the one-time `code` Google hands
     * back is exchanged here for the same credentials the email endpoints
     * return. A mutation rather than a query because the code burns on use —
     * there is nothing worth caching, and a refetch would fail.
     */
    googleAuth: builder.mutation({
      /**
       * `remember` is the sign-in form's checkbox, carried across the trip to
       * Google (see utilities/googleAuth.js) — a trader who ticked it before
       * pressing the Google button meant it for this session too.
       */
      query: ({ code }) => ({
        url: '/google/user/profile/',
        params: { code },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        dispatch(loginStart());
        try {
          const { data } = await queryFulfilled;
          // 2FA on: no session yet, just a `temp_token` for `verify2fa` — the
          // callback page opens the same challenge the email sign-in does.
          if (data?.requires_2fa) {
            dispatch(twoFactorRequired());
            return;
          }
          if (!data?.idToken) {
            dispatch(loginFailure(t(GOOGLE_FAILED_MESSAGE)));
            return;
          }
          dispatch(loginSuccess({ ...data, remember: Boolean(arg?.remember) }));
          dispatch(kycaidApi.endpoints.getIsNigeria.initiate());
        } catch (err) {
          dispatch(loginFailure(authErrorMessage(err?.error)));
        }
      },
      invalidatesTags: ['Auth'],
    }),
  }),
});

export const {
  useRequestPasswordResetMutation,
  useLoginMutation,
  useChangePasswordMutation,
  useRenewSessionMutation,
  useVerify2faMutation,
  useSignupMutation,
  useGoogleAuthMutation,
} = authApi;
